import { createSelector } from '@reduxjs/toolkit';

import type { AssetsUser } from '@iconik-io/iconik-api/src/services/assets';
import type {
  UsersUserElasticSchemaWithId,
  UsersUserSchema,
} from '@iconik-io/iconik-api/src/services/users';

import { createEntitiesIndexSelector } from '../../entities_index/selectors';
import type { BaseAppState } from '../../types';
import { getEntityById } from '../utils';

function pickAssetsUserData(
  user: UsersUserElasticSchemaWithId | UsersUserSchema,
): AssetsUser {
  return {
    id: user.id,
    first_name: user.first_name ?? undefined,
    last_name: user.last_name ?? undefined,
    email: user.email,
    photo: user.photo,
    photo_big: user.photo_big,
    photo_small: user.photo_small,
  };
}

const getSegment = (state: BaseAppState, segmentId: string) =>
  getEntityById(state, 'segments', segmentId);

const getUser = (state: BaseAppState, segmentId: string) => {
  const segment = state.entities.segments[segmentId];
  if (!segment?.user_id) {
    return undefined;
  }
  const userId = segment.user_id;
  return (
    getEntityById(state, 'users_basic', userId) ??
    getEntityById(state, 'users', userId)
  );
};

export const selectUserInfoBySegmentId = createSelector(
  [getSegment, getUser],
  (segment, user): AssetsUser | undefined => {
    if (!segment) {
      return undefined;
    }

    let result: AssetsUser | undefined;

    if (!result && user) {
      result = pickAssetsUserData(user);
    }

    if (!result && segment.share_user_email) {
      return {
        id: segment.user_id,
        first_name: segment.share_user_email,
        last_name: '',
        email: segment.share_user_email,
      };
    }

    if (!result && segment.external_id) {
      return {
        id: segment.user_id,
        first_name: segment.external_id.split(':')[3],
        last_name: '',
      };
    }

    return result;
  },
);

export const selectSegmentsByAssetId = createEntitiesIndexSelector(
  'segments',
  'asset_id',
);

const getVersionId = (
  _state: BaseAppState,
  _assetId?: string,
  versionId?: string,
  _shareId?: string,
) => versionId;

const getAssetId = (
  _state: BaseAppState,
  assetId?: string,
  _versionId?: string,
) => assetId;

const getShareId = (
  _state: BaseAppState,
  _assetId?: string,
  _versionId?: string,
  shareId?: string,
) => shareId;

const getIsShowExistingComments = (
  _state: BaseAppState,
  _assetId?: string,
  _versionId?: string,
  _shareId?: string,
  showExistingComments?: boolean,
) => showExistingComments;

export const selectSegmentsByAssetVersionId = createSelector(
  [selectSegmentsByAssetId, getVersionId, getAssetId],
  (segments, versionId, assetId) => {
    return segments.filter((segment) => {
      return segment.version_id === versionId || segment.asset_id !== assetId;
    });
  },
);

export const selectSegmentsByAssetVersionIdShareId = createSelector(
  [
    selectSegmentsByAssetId,
    getVersionId,
    getShareId,
    getIsShowExistingComments,
  ],
  (segments, versionId, shareId, showExistingComments) => {
    return segments.filter((segment) => {
      const enableShowExistingComments = showExistingComments
        ? true
        : segment.share_id === shareId;
      return segment.segment_type === 'COMMENT'
        ? segment.version_id === versionId && enableShowExistingComments
        : segment.version_id === versionId;
    });
  },
);
